import React, { useEffect, useState, useRef, useContext } from "react";
import ScheduleBetaContext from "Pages/MyScheduleBeta/Context/ScheduleBetaContext";

export interface DayTimeGridProps {
  /** Start hour in 24h format (e.g. 7 for 7 AM) */
  startHour?: number;
  /** End hour in 24h format (e.g. 19 for 7 PM) */
  endHour?: number;
  /** Height in pixels for one hour block */
  hourHeight?: number;
  /** Whether to show the red current time indicator */
  showCurrentTime?: boolean;
  /** The selected date (YYYY-MM-DD or Date) */
  selectedDate?: string | Date;
  /** Today's date (YYYY-MM-DD or Date) */
  todayDate?: string | Date;
  /** Children which will be positioned absolutely over the grid */
  children?: React.ReactNode;
  /** Ref to the scrollable parent container */
  scrollContainerRef?: React.RefObject<HTMLDivElement>;
}

const DayTimeGrid: React.FC<DayTimeGridProps> = ({
  startHour = 7,
  endHour = 19,
  hourHeight = 80, // Default 120px per hour for spacious look
  showCurrentTime = true,
  selectedDate: selectedDateProp,
  todayDate: todayDateProp,
  children,
  scrollContainerRef,
}) => {
  const scheduleContext = useContext(ScheduleBetaContext);
  const selectedDate = selectedDateProp ?? scheduleContext?.selectedDate;
  const todayDate = todayDateProp ?? scheduleContext?.todayDate;

  const [currentTime, setCurrentTime] = useState(new Date());
  const currentTimeRef = useRef<HTMLDivElement>(null);

  const isCurrentDateToday = () => {
    if (!selectedDate) return true;

    if (
      todayDate &&
      typeof selectedDate === "string" &&
      typeof todayDate === "string"
    ) {
      return selectedDate === todayDate;
    }

    if (typeof selectedDate === "string") {
      const parts = selectedDate.split("-");
      if (parts.length === 3) {
        const year = parseInt(parts[0], 10);
        const month = parseInt(parts[1], 10) - 1;
        const day = parseInt(parts[2], 10);
        return (
          currentTime.getFullYear() === year &&
          currentTime.getMonth() === month &&
          currentTime.getDate() === day
        );
      }
    }

    const d =
      selectedDate instanceof Date ? selectedDate : new Date(selectedDate);
    return (
      d.getFullYear() === currentTime.getFullYear() &&
      d.getMonth() === currentTime.getMonth() &&
      d.getDate() === currentTime.getDate()
    );
  };

  const isToday = isCurrentDateToday();

  useEffect(() => {
    if (!showCurrentTime || !isToday) return;
    const interval = setInterval(() => {
      setCurrentTime(new Date());
    }, 60000); // update every minute
    return () => clearInterval(interval);
  }, [showCurrentTime, isToday]);

  useEffect(() => {
    if (showCurrentTime && isToday && currentTimeRef.current) {
      setTimeout(() => {
        const timeIndicator = currentTimeRef.current;
        if (!timeIndicator) return;

        // Use the parent scroll container if provided, otherwise find the nearest scrollable ancestor
        const container =
          scrollContainerRef?.current ||
          // eslint-disable-next-line quotes
          timeIndicator.closest<HTMLElement>('[style*="overflow"]');
        if (!container) return;

        // Calculate the offset of the time indicator relative to the container
        const indicatorRect = timeIndicator.getBoundingClientRect();
        const containerRect = container.getBoundingClientRect();
        const indicatorOffsetInContainer =
          indicatorRect.top - containerRect.top + container.scrollTop;
        const containerHeight = container.clientHeight;

        // Scroll so the current time indicator is centered in the container
        container.scrollTo({
          top: indicatorOffsetInContainer - containerHeight / 2,
          behavior: "smooth",
        });
      }, 100);
    }
  }, [showCurrentTime, scrollContainerRef]);

  const hours = [];
  for (let i = startHour; i <= endHour; i++) {
    hours.push(i);
  }

  const formatHour = (hour: number) => {
    if (hour === 0) return "12 AM";
    if (hour < 12) return `${hour} AM`;
    if (hour === 12) return "12 PM";
    return `${hour - 12} PM`;
  };

  const formatExactTime = (date: Date) => {
    let h = date.getHours();
    const m = String(date.getMinutes()).padStart(2, "0");
    const ampm = h >= 12 ? "pm" : "am";
    h = h % 12;
    if (h === 0) h = 12;
    return `${h}:${m}${ampm}`;
  };

  const currentHour = currentTime.getHours();
  const currentMinute = currentTime.getMinutes();

  // Only show the red line if the current time is within our grid bounds
  const isCurrentTimeInRange =
    (currentHour > startHour ||
      (currentHour === startHour && currentMinute >= 0)) &&
    (currentHour < endHour || (currentHour === endHour && currentMinute === 0));

  // Top position of the red line relative to the grid wrapper
  const redLineTop =
    (currentHour - startHour + currentMinute / 60) * hourHeight;

  return (
    <div style={styles.container}>
      <div style={styles.gridWrapper}>
        {/* Time Labels & Grid Lines */}
        {hours.map((hour, index) => (
          <div
            key={hour}
            style={{
              ...styles.row,
              height: index !== hours.length - 1 ? hourHeight : 0,
            }}
          >
            <div style={styles.timeLabelWrapper}>
              <span style={styles.timeLabel}>{formatHour(hour)}</span>
            </div>
            <div style={styles.gridLine} />
          </div>
        ))}

        {/* Current Time Indicator */}
        {showCurrentTime && isCurrentTimeInRange && isToday && (
          <div
            ref={currentTimeRef}
            style={{ ...styles.currentTimeWrapper, top: redLineTop }}
          >
            <div style={styles.currentTimeLabelWrapper}>
              <span style={styles.currentTimeLabel}>
                {formatExactTime(currentTime)}
              </span>
            </div>
            <div style={styles.currentTimeLineContainer}>
              <div style={styles.currentTimeDot} />
              <div style={styles.currentTimeLine} />
            </div>
          </div>
        )}

        {/* Children (Cards) Container */}
        <div style={styles.contentContainer}>{children}</div>
      </div>
    </div>
  );
};

const styles: Record<string, React.CSSProperties> = {
  container: {
    width: "100%",
    backgroundColor: "#ffffff",
    position: "relative",
    paddingTop: "20px", // Padding so the top label is not cut off
    paddingBottom: "20px",
  },
  gridWrapper: {
    position: "relative",
    width: "100%",
    display: "flex",
    flexDirection: "column",
  },
  row: {
    display: "flex",
    flexDirection: "row",
    alignItems: "flex-start", // align to top so line is exactly at the hour
    width: "100%",
    position: "relative",
  },
  timeLabelWrapper: {
    width: "64px",
    flexShrink: 0,
    display: "flex",
    justifyContent: "flex-end",
    paddingRight: "16px",
    transform: "translateY(-50%)", // perfectly center on the line
  },
  timeLabel: {
    fontSize: "12px",
    color: "#9e9e9e",
    fontWeight: 500,
    textTransform: "uppercase",
  },
  gridLine: {
    flex: 1,
    height: "1px",
    backgroundColor: "#f0f0f0",
  },
  currentTimeWrapper: {
    position: "absolute",
    left: 0,
    right: 0,
    display: "flex",
    flexDirection: "row",
    alignItems: "center",
    zIndex: 10,
    transform: "translateY(-50%)", // center on the calculated top
  },
  currentTimeLabelWrapper: {
    width: "64px",
    flexShrink: 0,
    display: "flex",
    justifyContent: "flex-end",
    paddingRight: "12px",
  },
  currentTimeLabel: {
    fontSize: "12px",
    color: "#e53935", // red color
    fontWeight: 600,
  },
  currentTimeLineContainer: {
    flex: 1,
    display: "flex",
    alignItems: "center",
    position: "relative",
  },
  currentTimeDot: {
    width: "8px",
    height: "8px",
    borderRadius: "50%",
    backgroundColor: "#e53935",
    position: "absolute",
    left: "-4px", // half of width to center on the start of the line
  },
  currentTimeLine: {
    width: "100%",
    height: "2px",
    backgroundColor: "#e53935",
  },
  contentContainer: {
    position: "absolute",
    top: 0,
    left: "64px",
    right: 0,
    bottom: 0,
    zIndex: 5,
  },
};

export default DayTimeGrid;
